// built-from: /application-navigation-shell v2 impl:application-navigation-shell role:implementation
// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  Telescope,
  Workflow,
  Box,
  Server,
  BadgeCheck,
  ArrowUpNarrowWide,
  Network,
  GitBranch,
  Building2,
  Search,
  ChartNoAxesCombined,
  FileSearch,
  FileText,
  FolderTree,
  Target,
  ClipboardList,
  Scale,
  Settings2,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import DreamDevLogo from './DreamDevLogo';
import EngagementSwitcher from './EngagementSwitcher';
import { useEngagement } from '../hooks/useEngagement';
import { useVisionSession } from '../hooks/useVisionSession';
import { isLegacySurfacesEnabled } from '../utils/featureFlags';
import '../styles/AppShell.css';

// AppShell — the persistent application chrome (007-dashboard-shell). A left
// sidebar carrying the brand lockup and the programme navigation, plus a top
// bar; the routed surface renders as `children` in the main area. Aligned to
// Mockups/ArcPilot Lite Dashboard.dc.html and re-skinned to the app-wide
// DreamDev-blue brand pivot. Tokens only; blue is the everyday primary, coral
// is reserved and is NOT used here.

export type AppShellNavItem = {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Optional count badge (e.g. open validation items). */
  badge?: number;
  /** Destinations that belong INSIDE this one (spec 100, decision 11: the
   * engagement's activities under Activities). They step aside with the
   * navigation when the sidebar collapses to its icon rail. */
  children?: AppShellNavItem[];
};

export type AppShellNavigation = {
  label: string;
  items: AppShellNavItem[];
  sections?: Array<{ label: string; items: AppShellNavItem[] }>;
};

export type AppShellProps = {
  children?: React.ReactNode;
  dock?: boolean;
  /**
   * Optional destination model for a bounded product journey. The shell still
   * owns rendering, active-state behaviour, branding and engagement context.
   * Omitting this preserves ArcPilot's current production navigation exactly.
   */
  navigation?: AppShellNavigation;
};

export const MODERNISATION_NAVIGATION: AppShellNavigation = {
  label: 'Modernisation engagement',
  items: [
    { to: '/engagement', label: 'Overview', Icon: LayoutGrid },
    { to: '/engagement/direction', label: 'Direction', Icon: Target },
    { to: '/engagement/discovery', label: 'Discovery', Icon: Search },
    { to: '/engagement/findings', label: 'Findings', Icon: FileSearch },
    { to: '/engagement/programme', label: 'Modernisation plan', Icon: ChartNoAxesCombined },
  ],
};

export const ENGAGEMENT_DRAFT_NAVIGATION: AppShellNavigation = {
  label: 'Engagement setup',
  items: [
    { to: '/engagement', label: 'Set up engagement', Icon: Settings2 },
  ],
};

export const ENGAGEMENT_ACTIVE_NAVIGATION: AppShellNavigation = {
  label: 'Engagement',
  items: [
    { to: '/engagement', label: 'Overview', Icon: LayoutGrid },
    { to: '/engagement/brief', label: 'Brief', Icon: FileText },
    { to: '/engagement/business-map', label: 'Business map', Icon: Box },
    { to: '/engagement/activities', label: 'Activities', Icon: ClipboardList },
    { to: '/engagement/evidence', label: 'Evidence & findings', Icon: FileSearch },
    { to: '/engagement/decisions', label: 'Decisions', Icon: Scale },
    // 090-engagement-knowledge-tree: the engagement's files, alongside the other
    // active-engagement destinations.
    { to: '/engagement/files', label: 'Files', Icon: FolderTree },
  ],
};

const MAIN_NAV: AppShellNavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutGrid },
  { to: '/engagement', label: 'Modernisation engagement', Icon: ChartNoAxesCombined },
  // 055-vision-entry-point: the Vision entry is RESTORED — but not here. The
  // facilitated vision session is session-keyed (/facilitation/:sessionId), so
  // there is no static `to` for a NavItem; the entry renders as VisionNavRow
  // below — a nav BUTTON whose activation resolves a session (reuse the
  // remembered open one, else POST /api/sessions) and then navigates. This
  // supersedes the 054 T052 removal (Ruling 3's "honest absence beats a dead
  // link"): the owner ruled the missing affordance a defect, and the
  // resolve-then-navigate button is a live link, not a dead one. It must NEVER
  // target the retired legacy vision route (legacyVisionRemoved detector 5).
  { to: '/streams', label: 'Value streams', Icon: Workflow },
  { to: '/domains', label: 'Domains', Icon: Box },
  { to: '/systems', label: 'Systems', Icon: Server },
];

const DECIDE_NAV: AppShellNavItem[] = [
  { to: '/validation', label: 'Validation', Icon: BadgeCheck, badge: 7 },
  { to: '/prioritisation', label: 'Prioritisation', Icon: ArrowUpNarrowWide },
  { to: '/golden-thread', label: 'Golden thread', Icon: Network },
];

const SETUP_NAV: AppShellNavItem[] = [
  { to: '/organisation', label: 'Organisation', Icon: Building2 },
  { to: '/sources', label: 'Sources', Icon: GitBranch },
];

// The restored Vision entry (055): a nav-idiom BUTTON, not a NavLink — the
// destination is session-keyed, so activation first resolves the session
// (useVisionSession: remembered-open → reuse; else create) and then navigates
// to /facilitation/:sessionId. Active state mirrors NavLink's aria-current
// idiom, keyed on the facilitation route.
const VisionNavRow: React.FC = () => {
  const { openVision } = useVisionSession();
  const { pathname } = useLocation();
  const isActive = pathname === '/facilitation' || pathname.startsWith('/facilitation/');
  return (
    <button
      type="button"
      className={`ap-shell-nav-link ap-shell-nav-link--button${
        isActive ? ' ap-shell-nav-link--active' : ''
      }`}
      aria-current={isActive ? 'page' : undefined}
      aria-label="Vision"
      title="Vision"
      onClick={() => {
        void openVision();
      }}
    >
      <Telescope size={19} aria-hidden="true" />
      <span className="ap-shell-nav-label">Vision</span>
    </button>
  );
};

const NavRow: React.FC<{ item: AppShellNavItem; nested?: boolean }> = ({ item, nested = false }) => {
  const { to, label, Icon, badge } = item;
  return (
    <NavLink
      to={to}
      end={to === '/' || to === '/engagement'}
      aria-label={item.label}
      title={item.label}
      className={({ isActive }) =>
        `ap-shell-nav-link${nested ? ' ap-shell-nav-link--child' : ''}${isActive ? ' ap-shell-nav-link--active' : ''}`
      }
    >
      <Icon size={nested ? 15 : 19} aria-hidden="true" />
      <span className="ap-shell-nav-label">{label}</span>
      {typeof badge === 'number' && badge > 0 && (
        <span className="ap-shell-nav-badge" aria-hidden="true">
          {badge}
        </span>
      )}
    </NavLink>
  );
};

// A destination and the destinations inside it (100, decision 11). The list
// is named by its parent, so a screen reader hears "Activities, list" before
// the engagement's own activities.
const NavGroupRow: React.FC<{ item: AppShellNavItem }> = ({ item }) => (
  <>
    <NavRow item={item} />
    {item.children?.length ? (
      <ul className="ap-shell-nav-children" aria-label={item.label}>
        {item.children.map((child) => (
          <li key={child.to}><NavRow item={child} nested /></li>
        ))}
      </ul>
    ) : null}
  </>
);

// User-controlled sidebar collapse (087 polish: canvas surfaces get
// crowded). Collapsing reuses the icon rail the narrow-viewport media query
// already ships; the choice persists per browser. Storage faults degrade to
// expanded.
const NAV_COLLAPSED_KEY = 'ap-nav-collapsed';
function readNavCollapsed(): boolean {
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

// 096: a screen may take over the sidebar. The shell keeps its head (brand,
// collapse) and the engagement switcher, and hands the middle to whoever
// claims it; the programme navigation steps aside while a claim holds.
// The claim is a portal target, so the screen's own component tree (hooks,
// router context) renders into the sidebar without the shell knowing what
// it is. Outside the shell (tests, previews) there is no target and the
// screen renders its navigation inline.
export interface ShellSidebarSlot {
  target: HTMLElement | null;
  claim: (retainNavigation?: boolean) => () => void;
}

const ShellSidebarContext = React.createContext<ShellSidebarSlot>({ target: null, claim: () => () => {} });

export function useShellSidebar(enabled = true, retainNavigation = false): HTMLElement | null {
  const { target, claim } = React.useContext(ShellSidebarContext);
  React.useEffect(() => enabled ? claim(retainNavigation) : undefined, [claim, enabled, retainNavigation]);
  return enabled ? target : null;
}

const AppShell: React.FC<AppShellProps> = ({ children, dock = false, navigation }) => {
  const [sidebarSlot, setSidebarSlot] = React.useState<HTMLElement | null>(null);
  const [sidebarClaims, setSidebarClaims] = React.useState(0);
  const [appendClaims, setAppendClaims] = React.useState(0);
  const [navCollapsed, setNavCollapsed] = React.useState(readNavCollapsed);
  const [narrow, setNarrow] = React.useState(() => window.matchMedia?.('(max-width: 760px)').matches || false);
  React.useEffect(() => {
    const media = window.matchMedia?.('(max-width: 760px)');
    if (!media) return;
    const update = () => setNarrow(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const claimSidebar = React.useCallback((retainNavigation = false) => {
    if (retainNavigation) {
      setAppendClaims(count => count + 1);
      return () => setAppendClaims(count => count - 1);
    }
    setSidebarClaims((count) => count + 1);
    return () => setSidebarClaims((count) => count - 1);
  }, []);
  const sidebarSlotValue = React.useMemo<ShellSidebarSlot>(
    () => ({ target: navCollapsed || narrow ? null : sidebarSlot, claim: claimSidebar }),
    [sidebarSlot, claimSidebar, navCollapsed, narrow],
  );
  const sidebarClaimed = sidebarClaims > 0;
  // 027 Slice C — the chip + the top bar derive from the SAME current engagement
  // (useEngagement().currentEngagement) so the two on-screen anchors can never
  // disagree (SC-002). The chip itself is the EngagementSwitcher trigger. When no
  // engagement is resolved (no provider / pre-resolve) the top bar keeps its
  // honest pre-engagement floor ("Programme" / "Modernisation overview").
  const { currentEngagement } = useEngagement();
  const eyebrow = currentEngagement ? 'Engagement' : 'Programme';
  const heading = currentEngagement
    ? `${currentEngagement.client} · ${currentEngagement.title || currentEngagement.programme}`
    : 'Modernisation overview';
  const activeNavigation = navigation ?? (
    isLegacySurfacesEnabled() ? undefined : MODERNISATION_NAVIGATION
  );
  const toggleNav = () => {
    setNavCollapsed((collapsed) => {
      const next = !collapsed;
      try { window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* keep in-memory */ }
      return next;
    });
  };

  return (
  // `dock` bounds the shell to the viewport so a full-height surface (e.g. the
  // vision board) fills the content area instead of overflowing it — the topbar
  // stays docked and the surface owns its own internal scroll (051). Opt-in:
  // document-style routes omit it and keep body scrolling.
  <ShellSidebarContext.Provider value={sidebarSlotValue}>
  <div className={`app-shell${dock ? ' app-shell--dock' : ''}${navCollapsed ? ' app-shell--nav-collapsed' : ''}`}>
    {/* ── Sidebar ─────────────────────────────────────────────────────── */}
    <aside className="ap-shell-sidebar">
      <div className="ap-shell-sidebar-head">
        <div className="ap-shell-brand">
          <DreamDevLogo variant="lockup" height={24} color="var(--ap-brand)" title="DreamDev" />
        </div>
        <button
          type="button"
          className="ap-shell-nav-collapse"
          aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          onClick={toggleNav}
        >
          {navCollapsed
            ? <PanelLeftOpen size={17} aria-hidden="true" />
            : <PanelLeftClose size={17} aria-hidden="true" />}
        </button>
      </div>

      <div className={`ap-shell-sidebar-slot${appendClaims ? ' ap-shell-sidebar-slot--append' : ''}`} ref={setSidebarSlot} hidden={!sidebarClaimed && !appendClaims} />

      {sidebarClaimed ? null : (
      <nav className="ap-shell-nav" aria-label={activeNavigation?.label ?? 'Programme'}>
        {(activeNavigation?.items ?? MAIN_NAV).map((item) => (
          <React.Fragment key={item.to}>
            <NavGroupRow item={item} />
            {/* Vision sits in its legacy slot, right after Dashboard (055). */}
            {!activeNavigation && item.to === '/' && <VisionNavRow />}
          </React.Fragment>
        ))}

        {(activeNavigation
          ? activeNavigation.sections ?? []
          : [
              { label: 'Decide', items: DECIDE_NAV },
              { label: 'Setup', items: SETUP_NAV },
            ]
        ).map((section) => (
          <React.Fragment key={section.label}>
            <div className="ap-shell-nav-group">{section.label}</div>
            {section.items.map((item) => (
              <NavRow key={item.to} item={item} />
            ))}
          </React.Fragment>
        ))}
      </nav>
      )}

      <EngagementSwitcher />
    </aside>

    {/* ── Main ────────────────────────────────────────────────────────── */}
    <main className="ap-shell-main">
      <header className="ap-shell-topbar">
        <div className="ap-shell-topbar-title">
          <div className="ap-shell-eyebrow">{eyebrow}</div>
          <div className="ap-shell-heading">{heading}</div>
        </div>
        <div className="ap-shell-topbar-spacer" />
        <button type="button" className="ap-shell-search" aria-label="Jump to… (Cmd+K)">
          <Search size={17} aria-hidden="true" />
          <span className="ap-shell-search-text">Jump to…</span>
          <span className="ap-shell-search-kbd" aria-hidden="true">
            ⌘K
          </span>
        </button>
      </header>

      <div className={`ap-shell-content${dock ? ' ap-shell-content--dock' : ''}`}>{children}</div>
    </main>
  </div>
  </ShellSidebarContext.Provider>
  );
};

export default AppShell;
